import { KeyConfigurations } from '@enums/KeyConfigurations';
import React, { ReactNode, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './index.module.scss';

const Modal = ({
  className,
  open,
  setOpen,
  onTapBackgroundClose,
  overlay,
  children,
  role,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
}: ModalProps) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleBodyScroll = () => {
      document.body.classList.toggle('modal-open', open);
      document.body.style.overflow = open ? 'hidden' : 'auto';
    };

    handleBodyScroll();

    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = 'auto';
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      // Store the element that had focus before opening the modal
      previouslyFocusedElement.current = document.activeElement as HTMLElement;

      // Set focus to the first focusable element in the modal
      setTimeout(() => {
        if (modalRef.current) {
          const focusableElements = modalRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
          );
          if (focusableElements.length > 0) {
            (focusableElements[0] as HTMLElement).focus();
          } else {
            // If no focusable elements, focus the modal itself
            modalRef.current.focus();
          }
        }
      }, 50);
    } else if (previouslyFocusedElement.current) {
      // Return focus to the previously focused element when modal closes
      previouslyFocusedElement.current.focus();
    }
  }, [open]);

  // Handle keyboard events
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) return;

    // Close on ESC key
    if (e.key === KeyConfigurations.ESCAPE) {
      setOpen(false);

      return;
    }

    // Trap focus inside modal
    if (e.key === 'Tab' && modalRef.current) {
      const focusableElements = modalRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      // If shift+tab on first element, move to last element
      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
      // If tab on last element, move to first element
      else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  };

  if (!open) return null;

  const modalMarkup = (
    <div className={styles.modalContainer} role="presentation" onKeyDown={handleKeyDown}>
      {overlay && (
        <div
          className={styles.overlay}
          onClick={() => onTapBackgroundClose && setOpen(false)}
          style={{ height: window.innerHeight + 1500 }}
          aria-hidden="true"
        />
      )}
      <div
        ref={modalRef}
        className={[styles.root, className].filter(Boolean).join(' ')}
        role={role}
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        tabIndex={-1} // Allow the modal itself to receive focus if needed
      >
        {children}
      </div>
    </div>
  );

  if (typeof document !== 'undefined' && document.body) {
    return createPortal(modalMarkup, document.body);
  }

  return modalMarkup;
};

Modal.defaultProps = {
  className: '',
  overlay: false,
  children: null,
  onTapBackgroundClose: false,
  role: 'dialog',
  'aria-labelledby': '',
  'aria-describedby': '',
};

interface ModalProps {
  className?: string;
  open: boolean;
  setOpen: (value: boolean) => void;
  onTapBackgroundClose?: boolean;
  overlay?: boolean;
  children?: ReactNode;
  role?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
}

export default Modal;
